import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LocationInitService } from '../../../core/services/location-init.service';
import { PlatformDataService } from '../../../core/services/platform-data.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperBannerComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { CoexistenceEntryService } from '../../../core/services/coexistence-entry.service';
import { LocationStateService } from '../../../core/services/location-state.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../core/analytics/events';
import { continuationUrl, validContextId } from '../../../core/utils/coexistence-entry';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'im-coexistence-entry',
  standalone: true,
  imports: [PaperBannerComponent, PaperButtonComponent, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (entry.snapshot(); as status) {
      @if (status.state !== 'ready') {
        <paper-banner tone="warning" icon="alert-triangle" [compact]="true" ariaLive="polite" data-testid="coexistence-entry" class="ph-no-capture">
          <div class="entry-content">
          <span>{{ ('coexistence.entry.' + status.state) | transloco }}</span>
          @if (destination(); as url) {
            <a [href]="url" target="_blank" rel="noopener noreferrer" class="ph-no-capture" data-testid="coexistence-continue" (click)="record('continue')">{{ 'coexistence.entry.continue_v2' | transloco }}</a>
            <span>{{ 'coexistence.entry.preserve_work' | transloco }}</span>
          } @else if (status.state === 'other-app') {
            <span>{{ 'coexistence.entry.no_destination' | transloco }}</span>
          }
          @if (status.state !== 'loading') {
            <paper-button intent="ghost" size="xs" class="ph-no-capture" (click)="retry()">{{ 'coexistence.entry.retry' | transloco }}</paper-button>
          }
          </div>
        </paper-banner>
      }
    }
    @if (bookingReference) {
      <span class="booking-reference">{{ 'coexistence.entry.booking_reference' | transloco }} {{ bookingReference }}</span>
    }
  `,
  styles: [`:host { display: contents; } .entry-content { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-3, 12px); } a { color: inherit; text-decoration: underline; } .booking-reference { color: var(--text-secondary); background: var(--card-bg-solid); padding: var(--space-2, 8px); font-size: var(--fs-body-sm); }`],
})
export class CoexistenceEntryComponent {
  readonly entry = inject(CoexistenceEntryService);
  private readonly location = inject(LocationStateService);
  private readonly analytics = inject(AnalyticsService);
  private readonly locationInit = inject(LocationInitService);
  private readonly platform = inject(PlatformDataService);
  private readonly router = inject(Router);
  private selectionGeneration = 0;
  private readonly params = new URLSearchParams(window.location.hash.split('?')[1] ?? window.location.search);
  readonly bookingReference = validContextId(this.params.get('coexistenceBooking')) ? this.params.get('coexistenceBooking') : null;
  readonly destination = computed(() => {
    const status = this.entry.snapshot();
    return status ? continuationUrl(status, environment.COEXISTENCE_EMPLOYEE_ORIGIN, {
      date: this.location.scrubberDate(), bookingId: this.bookingReference ?? undefined,
    }, { enabled: environment.COEXISTENCE_LAB, development: environment.production === false && environment.COEXISTENCE_ENTRY_ENABLED, currentOrigin: window.location.origin }) : null;
  });
  constructor() {
    if (this.entry.enabled) {
      void this.initialiseLocation(window.location.hash || window.location.search);
      this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
        if (event instanceof NavigationEnd && event.urlAfterRedirects.includes('location=')) void this.initialiseLocation(event.urlAfterRedirects);
      });
    }
    effect(() => {
      const locationId = this.location.selectedLocationId();
      untracked(() => locationId ? void this.entry.refresh(locationId) : this.entry.clear());
    });
  }
  private async initialiseLocation(url: string): Promise<void> {
    const generation = ++this.selectionGeneration;
    const requested = new URLSearchParams((url.split('?')[1] ?? '').split('#')[0]).get('location');
    try {
      // Choosing an office and its owning app must not depend on a map provider key.
      await this.locationInit.ensureLocationSelected();
      if (generation !== this.selectionGeneration || !validContextId(requested)) return;
      const location = this.platform.locationsList().find(value => value._id === requested);
      if (location) {
        this.location.selectLocation(location._id);
        this.location.setTimezone(location.timezone);
      }
    } catch {
      if (generation === this.selectionGeneration) this.entry.setBlocked(requested ?? '', 'unavailable');
    }
  }
  retry(): void {
    this.record('retry');
    const locationId = this.entry.snapshot()?.locationId;
    if (locationId) void this.entry.refresh(locationId);
  }
  record(action: 'continue' | 'retry'): void {
    const snapshot = this.entry.snapshot();
    if (!snapshot) return;
    this.analytics.capture(ANALYTICS_EVENTS.COEXISTENCE_ENTRY_ACTION, {
      action, location_id: snapshot.locationId, state: snapshot.state, destination_app: snapshot.serving?.owner ?? 'unknown',
    });
  }
}
